import { Head, usePage } from "@inertiajs/react";
import { motion } from "motion/react";
import { useMemo, useState } from "react";

import { Container } from "@/theme1/components/common/Container";
import {ProductGrid} from "@/theme1/components/product/ProductGrid";

import ActiveFilters from "@/theme1/components/shop/ActiveFilters";
import EmptyProducts from "@/theme1/components/shop/EmptyProducts";
import FilterSidebar from "@/theme1/components/shop/FilterSidebar";
import MobileFilterDrawer from "@/theme1/components/shop/MobileFilterDrawer";
import ProductPagination from "@/theme1/components/shop/ProductPagination";
import ProductToolbar from "@/theme1/components/shop/ProductToolbar";
import ShopHeader from "@/theme1/components/shop/ShopHeader";

import { slugify } from "@/theme1/lib/productNormalizer";

const emptyFilters = {
  series: [],
  category: [],
  color: [],
  availability: [],
  rating: [],
  badges: [],
};

const perPage = 12;

export default function Shop() {
  const { products: pageProducts = [], shopFilterGroups = [] } = usePage().props;
  const initialCategory = (() => {
    if (typeof window === "undefined") return "";

    return new URLSearchParams(window.location.search).get("category") || "";
  })();
  const initialBrand = (() => {
    if (typeof window === "undefined") return "";

    return new URLSearchParams(window.location.search).get("brand") || "";
  })();

  const [filters, setFilters] = useState(() => ({
    ...emptyFilters,
    category: initialCategory ? [initialCategory] : [],
  }));
  const [brand, setBrand] = useState(initialBrand);
  const [price, setPrice] = useState({ min: "", max: "" });
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleFilterChange = (group, value) => {
    setPage(1);
    setFilters((current) => ({
      ...current,
      [group]: current[group].includes(value)
        ? current[group].filter((item) => item !== value)
        : [...current[group], value],
    }));
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setBrand("");
    setPrice({ min: "", max: "" });
    setSearch("");
    setPage(1);
  };

  const filteredProducts = useMemo(() => {
    let products = [...pageProducts];

    Object.entries(filters).forEach(([key, values]) => {
      if (!values.length) return;

      products = products.filter((product) => {
        if (key === "badges") {
          return values.some((value) => product.badges?.includes(value));
        }

        if (key === "rating") {
          return values.some((value) => {
            if (value === "4 Stars & Up") return product.rating >= 4;
            if (value === "4.5 Stars & Up") return product.rating >= 4.5;
            return true;
          });
        }

        if (key === "category") {
          const productCategory = slugify(product.category);
          return values.some((value) => slugify(value) === productCategory);
        }

        return values.some((value) => slugify(product[key]) === slugify(value));
      });
    });

    if (brand) {
      products = products.filter((product) => {
        const productBrand = product.brand || product.series || "";
        return slugify(productBrand) === slugify(brand);
      });
    }

    if (price.min) {
      products = products.filter((product) => product.price >= Number(price.min));
    }

    if (price.max) {
      products = products.filter((product) => product.price <= Number(price.max));
    }

    if (search.trim()) {
      const query = search.toLowerCase();

      products = products.filter((product) =>
          [product.name, product.category, product.series, product.brand, product.color]
          .join(" ")
          .toLowerCase()
          .includes(query)
      );
    }

    products.sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "popularity") return b.popularity - a.popularity;
      if (sort === "alphabetical") return a.name.localeCompare(b.name);
      return b.id - a.id;
    });

    return products;
  }, [brand, filters, pageProducts, price, search, sort]);

  const totalPages = Math.ceil(filteredProducts.length / perPage);
  const paginatedProducts = filteredProducts.slice((page - 1) * perPage, page * perPage);

  return (
    <>
      <Head>
        <title>Shop</title>
        <meta
          name="description"
          content="Shop premium caps, apparel, accessories, collections, and limited edition products."
        />
      </Head>

      <Container className="py-6 sm:py-8 lg:py-12">
        <div className="space-y-10">
      <ShopHeader
  title="Discover Our Collection"
  description="Explore premium caps, apparel, and accessories crafted for style and comfort."
  image="https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=1200&q=80"
  imageAlt="Fashion collection display"
/>

          <ProductToolbar
            total={filteredProducts.length}
            search={search}
            sort={sort}
            onSortChange={setSort}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            onOpenFilters={() => setDrawerOpen(true)}
          />

          <ActiveFilters
            filters={filters}
            price={price}
            onRemove={handleFilterChange}
          onClear={clearFilters}
        />

          <div className="grid gap-8 lg:grid-cols-[18rem_1fr]">
            <FilterSidebar
              filterGroups={shopFilterGroups}
              filters={filters}
              price={price}
              onFilterChange={handleFilterChange}
              onPriceChange={(value) => {
                setPrice(value);
                setPage(1);
              }}
              onClear={clearFilters}
            />

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
              className="space-y-10"
            >
              {paginatedProducts.length ? (
                <>
                  <ProductGrid products={paginatedProducts} />
                  <ProductPagination
                    page={page}
                    totalPages={totalPages}
                    onChange={setPage}
                  />
                </>
              ) : (
                <EmptyProducts onClear={clearFilters} />
              )}
            </motion.div>
          </div>
        </div>
      </Container>

      <MobileFilterDrawer
        filterGroups={shopFilterGroups}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        price={price}
        onFilterChange={handleFilterChange}
        onPriceChange={(value) => {
          setPrice(value);
          setPage(1);
        }}
        onClear={clearFilters}
      />
    </>
  );
}
